import { useId, type ChangeEvent, type InputHTMLAttributes, type ReactNode } from 'react';

/**
 * TextField — label + input + hint / error の 1 行 form 入力 (Sprint AS、docs/16 §10-2 P5 設計)。
 *
 * 設計方針:
 *   - native `<input>` を採用 (a11y / IME / browser autofill ベスト)
 *   - label と input を `htmlFor` / `id` で結ぶ。id 未指定なら `useId()` で生成
 *   - error / hint を `aria-describedby` で input に紐付け、error 時 `aria-invalid` を立てる
 *   - error と hint を同時指定した場合は両方 describedby に並べる (error 優先表示は視覚側のみ)
 *   - focus ring は既存 component (Tabs) と揃え primary-400 (tailwind preset に primary-500 未定義のため)
 *   - inline style は使わない (CLAUDE.md「shared-ui は token のみ」遵守)
 *
 * 想定 usage:
 *   <TextField label="表示名" value={name} onChange={setName} required hint="20 文字以内" />
 *   <TextField label="メール" type="email" value={email} onChange={setEmail} error={emailErr} />
 */

export type TextFieldType = 'text' | 'email' | 'tel' | 'password' | 'number';

export interface TextFieldProps {
  label: ReactNode;
  value: string;
  /** 値変化コールバック。文字列のみ渡す (event ではない) — shared-ui 内 form base の統一規約。 */
  onChange: (value: string) => void;
  placeholder?: string;
  type?: TextFieldType;
  required?: boolean;
  disabled?: boolean;
  /** error 文言。指定時は aria-invalid + danger 色で表示。 */
  error?: ReactNode;
  /** 補助 hint。slate-600 で表示。error 同時指定可。 */
  hint?: ReactNode;
  /** 明示 id。未指定なら useId() で生成。 */
  id?: string;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  /** 任意で input にそのまま渡す className (form layout 調整用). */
  className?: string;
}

const INPUT_BASE =
  'block w-full rounded-md border bg-white text-slate-900 placeholder-slate-400 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed';
const INPUT_NORMAL = 'border-slate-300 focus:border-primary-600 focus:ring-primary-400';
const INPUT_ERROR = 'border-danger-600 focus:border-danger-600 focus:ring-danger-600';

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  disabled,
  error,
  hint,
  id,
  autoComplete,
  className = '',
}: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);

  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-danger-600 ml-0.5">
            *
          </span>
        ) : null}
      </label>
      <input
        id={inputId}
        type={type}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        className={`${INPUT_BASE} ${invalid ? INPUT_ERROR : INPUT_NORMAL}`}
      />
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-danger-600">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="mt-1 text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
